import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export const SYMPTOM_FUNNEL_EVENT = "crs:symptom-funnel";

export interface SymptomFunnelDetail {
  message: string;
}

interface SymptomContextValue {
  selectedSymptoms: string[];
  toggleSymptom: (symptom: string) => void;
  isSelected: (symptom: string) => boolean;
  clearSymptoms: () => void;
  /** Builds the formatted message from selected symptoms (or "" if none),
   *  dispatches the funnel window event, and returns the message. */
  requestFunnel: () => string;
}

const SymptomContext = createContext<SymptomContextValue | null>(null);

export function SymptomProvider({ children }: { children: ReactNode }) {
  const [selectedSymptoms, setSelected] = useState<string[]>([]);

  const toggleSymptom = useCallback((symptom: string) => {
    setSelected((prev) =>
      prev.includes(symptom) ? prev.filter((s) => s !== symptom) : [...prev, symptom],
    );
  }, []);

  const isSelected = useCallback(
    (symptom: string) => selectedSymptoms.includes(symptom),
    [selectedSymptoms],
  );

  const clearSymptoms = useCallback(() => setSelected([]), []);

  const requestFunnel = useCallback(() => {
    const message =
      selectedSymptoms.length === 0
        ? ""
        : "I've noticed these symptoms on my property:\n" +
          selectedSymptoms.map((s) => `- ${s}`).join("\n");

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent<SymptomFunnelDetail>(SYMPTOM_FUNNEL_EVENT, {
          detail: { message },
        }),
      );
    }
    return message;
  }, [selectedSymptoms]);

  const value = useMemo<SymptomContextValue>(
    () => ({ selectedSymptoms, toggleSymptom, isSelected, clearSymptoms, requestFunnel }),
    [selectedSymptoms, toggleSymptom, isSelected, clearSymptoms, requestFunnel],
  );

  return <SymptomContext.Provider value={value}>{children}</SymptomContext.Provider>;
}

export function useSymptomContext(): SymptomContextValue {
  const ctx = useContext(SymptomContext);
  if (!ctx) {
    // Allow consumers (like tiles inside other LPs) to no-op safely.
    return {
      selectedSymptoms: [],
      toggleSymptom: () => {},
      isSelected: () => false,
      clearSymptoms: () => {},
      requestFunnel: () => "",
    };
  }
  return ctx;
}
